import { Suspense } from "react";
import { lazyWithRetry } from "./lib/lazyWithRetry";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import ScrollToTop from "./components/ScrollToTop";
import DeferredProviders from "./components/DeferredProviders";

// Lazy load all pages for code splitting
const Index = lazyWithRetry(() => import("./pages/Index"));
const Services = lazyWithRetry(() => import("./pages/Services"));
const ServiceDetail = lazyWithRetry(() => import("./pages/ServiceDetail"));
const About = lazyWithRetry(() => import("./pages/About"));
const Contact = lazyWithRetry(() => import("./pages/Contact"));
const Methodology = lazyWithRetry(() => import("./pages/Methodology"));
const CaseStudies = lazyWithRetry(() => import("./pages/CaseStudies"));
const Careers = lazyWithRetry(() => import("./pages/Careers"));
const PrivacyPolicy = lazyWithRetry(() => import("./pages/PrivacyPolicy"));
const BankReconciliationAutomation = lazyWithRetry(() => import("./pages/BankReconciliationAutomation"));
const NetSuiteReconciliationAutomation = lazyWithRetry(() => import("./pages/NetSuiteReconciliationAutomation"));
const NotFound = lazyWithRetry(() => import("./pages/NotFound"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
    },
  },
});

// Minimal loading fallback
const PageLoader = () => (
  <div className="min-h-screen bg-background flex items-center justify-center">
    <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
  </div>
);

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <DeferredProviders>
        <BrowserRouter>
          <ScrollToTop />
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/services" element={<Services />} />
              <Route path="/services/:slug" element={<ServiceDetail />} />
              <Route path="/about" element={<About />} />
              <Route path="/methodology" element={<Methodology />} />
              <Route path="/case-studies" element={<CaseStudies />} />
              <Route path="/careers" element={<Careers />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/blog/bank-reconciliation-automation" element={<BankReconciliationAutomation />} />
              <Route path="/blog/netsuite-reconciliation-automation-guide" element={<NetSuiteReconciliationAutomation />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </DeferredProviders>
    </QueryClientProvider>
  </HelmetProvider>
);

export default App;
